import { FC, MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import qualityEvents from '../../../../services/quality-events/quality-events';
import enhancedEcommerce, { PaymentOptionType } from '../../../../utils/enhanced-ecommerce';
import serializeQueryParams from '../../../../utils/url/serialize-query-params';
import cn from '../../../../utils/classnames';
import { AVAILABLE_PAYMENT_METHODS } from '../../payment.constants';
import { PAYMENT_METHOD_KEYS } from '../../../../constants/payment-method-keys';
import { changeState, resetErrors } from '../../reducers';
import { getActivePaymentMethodKey } from '../../payment.helpers';
import usePaymentOptions from '../../../../services/payment/use-payment-options';
import AdyenOption from './custom-options/adyen-option/adyen-option';
import PaypalOption from './custom-options/paypal-option/paypal-option';
import PaymentOption from './payment-option/payment-option';
import formatPriceToFloat from '../../../../utils/price/format-price-to-float';
import translation from '../../../../languages';
import FontAwesomeIcon from '../../../../ui/font-awesome-icon/font-awesome-icon';
import Button from '../../../../ui/button/button';
import { BUTTON_VARIANTS } from '../../../../constants/button-variants';
import schoolAccount from '../../../../utils/school-account';
import './payment-method-select.scss';

type PaymentMethodSelectProps = TranslationProps & {};

const defaultList = [
    PAYMENT_METHOD_KEYS.AMAZON_PAY,
    PAYMENT_METHOD_KEYS.IDEAL,
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD,
    PAYMENT_METHOD_KEYS.CARD,
    PAYMENT_METHOD_KEYS.APPLE_PAY,
    PAYMENT_METHOD_KEYS.GOOGLE_PAY,
    PAYMENT_METHOD_KEYS.INVOICE,
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA,
    PAYMENT_METHOD_KEYS.SEPA_PAY,
    PAYMENT_METHOD_KEYS.KLARNA_PAY,
    PAYMENT_METHOD_KEYS.BANCONTACT_CARD,
    PAYMENT_METHOD_KEYS.BANCONTACT_MOBILE,
    PAYMENT_METHOD_KEYS.CARTA_DEL_DOCENTE
];

const ppMethods = [
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD,
    PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA
];

const PaymentMethodSelect: FC<PaymentMethodSelectProps> = ({ t }) => {
    const queryParams = serializeQueryParams();
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.main.user);
    const currency = useSelector((state: RootState) => state.main.currency);
    const { activePaymentOption, paymentCart, failedPaymentMethods } = useSelector((state: RootState) => state.payment);
    const { data: paymentOptionsData } = usePaymentOptions(currency.code);

    const [paymentMethodsList, setPaymentMethodsList] = useState<PAYMENT_METHOD_KEYS[]>([]);
    const [areOtherPaymentMethodsExpanded, setAreOtherPaymentMethodsExpanded] = useState(false);

    const isSchoolAccount = Boolean(schoolAccount.hasSchool(user));
    const isInvoiceShownAsSecondary = !isSchoolAccount && ((paymentOptionsData?.allowedPaymentMethods ?? []) as PAYMENT_METHOD_KEYS[]).includes(PAYMENT_METHOD_KEYS.INVOICE);

    useEffect(() => {
        if (!paymentOptionsData) {
            return;
        }

        const processedPaymentMethodsList = defaultList
            .filter(method => {
                // For guest user which make purchase by PP Express we need to show only PP option
                if (queryParams.ppTransactionId) {
                    return method === PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT;
                }

                return paymentOptionsData?.allowedPaymentMethods?.includes(method);
            });

        setPaymentMethodsList(Array.from(new Set(processedPaymentMethodsList)));

    }, [paymentOptionsData]);

    useEffect(() => {
        if (
            activePaymentOption?.id
            && paymentMethodsList.length
            && !paymentMethodsList.includes(activePaymentOption?.id as PAYMENT_METHOD_KEYS)
            // Trick because PayPalCheckout and PayPalCheckoutCreditCard payment methods have another ids
            && !['PayPal', 'cc', 'Invoice'].includes(activePaymentOption?.id)
        ) {
            dispatch(changeState({ activePaymentOption: undefined }));
        }
    }, [activePaymentOption?.id, paymentMethodsList]);

    useEffect(() => {
        if (!paymentMethodsList.length) {
            return;
        }

        if (isInvoiceShownAsSecondary && paymentMethodsList.some(method => method === PAYMENT_METHOD_KEYS.INVOICE)) {
            setPaymentMethodsList(prev => {
                return prev.filter(method => method !== PAYMENT_METHOD_KEYS.INVOICE);
            })
        }
    }, [paymentMethodsList, isInvoiceShownAsSecondary]);

    useEffect(() => {
        if (!failedPaymentMethods?.length) {
            return;
        }

        const allowedPaymentMethods = paymentMethodsList.filter(
            (paymentMethod) => !failedPaymentMethods.includes(paymentMethod)
        );

        const insertReplacementMethod = (replacement: PAYMENT_METHOD_KEYS) => {
            // Insert at the second position (after Amazon) or at the beginning
            const positionIndex = allowedPaymentMethods.indexOf(defaultList[0]);

            allowedPaymentMethods.splice(positionIndex + 1, 0, replacement);
        };

        const hasFailedPPMethod = failedPaymentMethods.some((method) => ppMethods.includes(method));
        const hasFailedCardMethod = failedPaymentMethods.includes(PAYMENT_METHOD_KEYS.CARD);

        // If at least one of PP methods is failed (and Adyen Card hasn't also failed) needs to add Adyen Card method
        const shouldReplaceWithCard = hasFailedPPMethod && !hasFailedCardMethod;

        // If the Adyen Card method is failed (and no PP method has also failed) needs to add PayPal Credit Card method back
        const shouldReplaceWithPaypalCreditCard = hasFailedCardMethod && !hasFailedPPMethod;

        if (shouldReplaceWithCard) {
            insertReplacementMethod(PAYMENT_METHOD_KEYS.CARD);
        }

        if (shouldReplaceWithPaypalCreditCard) {
            insertReplacementMethod(PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD);
        }

        // @ts-ignore
        setPaymentMethodsList([...new Set(allowedPaymentMethods)]);

        const defaultPreselectedMethod = () => {
            if (shouldReplaceWithCard && activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD].id) {
                return AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.CARD];
            }

            if (shouldReplaceWithPaypalCreditCard && activePaymentOption?.id === AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.CARD].id) {
                return AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD];
            }

            return undefined;
        };

        handlePaymentMethodChange(defaultPreselectedMethod(), 0);
    }, [failedPaymentMethods?.length]);

    useEffect(() => {
        if (activePaymentOption?.id) {
            enhancedEcommerce.tracker.checkoutStep2(paymentCart?.cartItems ?? [], activePaymentOption?.id);
        }
    }, [activePaymentOption?.id]);

    const handlePaymentMethodChange = (method: PaymentOptionType | undefined, index: number) => {
        if (method) {
            const methodKey = getActivePaymentMethodKey(method.id)

            qualityEvents.checkoutPage.selectPaymentProvider({
                cartTotalValue: paymentCart?.finalPrice ?? '',
                paymentProvider: methodKey,
                paymentProviderPosition: index + 1
            });
        }

        dispatch(changeState({ activePaymentOption: method }));
        dispatch(resetErrors());
    };

    const getPaymentOption = (methodKey: PAYMENT_METHOD_KEYS, index: number) => {

        const method = AVAILABLE_PAYMENT_METHODS[methodKey];

        const handlePaymentMethodClick = (method: { id: string; title: string }): void =>
            handlePaymentMethodChange(method, index);

        switch (methodKey) {
            case PAYMENT_METHOD_KEYS.APPLE_PAY:
            case PAYMENT_METHOD_KEYS.GOOGLE_PAY:
            case PAYMENT_METHOD_KEYS.KLARNA_PAY:
            case PAYMENT_METHOD_KEYS.SEPA_PAY:
            case PAYMENT_METHOD_KEYS.CARD:
            case PAYMENT_METHOD_KEYS.IDEAL:
            case PAYMENT_METHOD_KEYS.BANCONTACT_CARD:
            case PAYMENT_METHOD_KEYS.BANCONTACT_MOBILE:
                return (
                    <AdyenOption
                        key={method.id}
                        type={method.id as PAYMENT_METHOD_KEYS}
                        isActive={activePaymentOption?.id === method.id}
                        onClick={handlePaymentMethodClick}
                    />
                );
            case PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT:
                return (
                    <PaypalOption
                        key={method.id}
                        isActive={activePaymentOption?.id === method.id}
                        onClick={handlePaymentMethodClick}
                    />
                );
            default:
                return (
                    <PaymentOption
                        key={method.id}
                        paymentMethod={method}
                        isActive={activePaymentOption?.id === method.id}
                        onClick={handlePaymentMethodClick}
                    />
                );
        }
    };

    const onToggleOtherPaymentMethods = (e: MouseEvent) => {
        e.preventDefault();

        if (activePaymentOption?.id === PAYMENT_METHOD_KEYS.INVOICE && areOtherPaymentMethodsExpanded) {
            handlePaymentMethodChange(AVAILABLE_PAYMENT_METHODS[paymentMethodsList[0]], 0);
        }

        if (!areOtherPaymentMethodsExpanded) {
            qualityEvents.checkoutPage.otherPaymentMethodsClick();
        }

        setAreOtherPaymentMethodsExpanded(prev => !prev);
    };

    return (
        <div className='payment-method-select'>
            <form>
                {paymentMethodsList.map((methodKey, index) => {
                    return getPaymentOption(methodKey, index);
                })}
                {isInvoiceShownAsSecondary && (
                    <div className={cn('invoice-payment-method-wrapper', { expanded: areOtherPaymentMethodsExpanded })}>
                        <Button
                            className='show-other-button'
                            variant={BUTTON_VARIANTS.SECONDARY}
                            onClick={onToggleOtherPaymentMethods}
                        >
                            <span>
                                <i>
                                    <FontAwesomeIcon name='minus' theme='gray' height={2} className={cn({ 'd-none': !areOtherPaymentMethodsExpanded })} />
                                    <FontAwesomeIcon name='plus' theme='gray' height={14} className={cn({ 'd-none': areOtherPaymentMethodsExpanded })} />
                                </i>
                                {t('payment.paymentMethodSelection.otherPaymentMethods')}
                            </span>
                        </Button>
                        <PaymentOption
                            paymentMethod={AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.INVOICE]}
                            isActive={activePaymentOption?.id === PAYMENT_METHOD_KEYS.INVOICE}
                            onClick={() => handlePaymentMethodChange(AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.INVOICE], paymentMethodsList.length)}
                        />
                    </div>
                )}
            </form>
        </div>
    );
};

const translate = translation('payment');

export default translate(PaymentMethodSelect);
